import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { amsPublicApiTokensGetOwnSessionPermissions } from 'service/amsApi';
import { isAmsAuthActive } from 'service/amsSession';
import type { Grant } from './grants';

export const sessionPermissionsQueryKey = ['ams', 'auth', 'permissions'] as const;

export function useHasGrants({ grants }: { grants: Grant[] }): { permitted: boolean; checking: boolean } {
  // Grants only exist on an AMS session; on the legacy path this never calls /ams/* and holds no grant.
  const enabled = isAmsAuthActive();
  const { data, isPending } = useQuery({
    queryKey: sessionPermissionsQueryKey,
    queryFn: async () => {
      const { data, error } = await amsPublicApiTokensGetOwnSessionPermissions();
      if (error) {
        console.error('Failed to load session permissions', error);
        throw new Error('Failed to load session permissions');
      }
      return data?.permission_codes ?? [];
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled,
  });

  const held = useMemo(() => new Set(data ?? []), [data]);

  return {
    permitted: grants.every((grant) => held.has(grant)),
    checking: enabled && isPending,
  };
}
